import { Banknote, LogOut, Monitor, RefreshCw } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/lib/api.ts";
import { useCurrentCashSession, useOpenCashSession } from "@/queries/cashSessions.ts";
import { useAuthStore } from "@/stores/auth.ts";
import { useTerminalStore } from "@/stores/terminal.ts";

export function OpenCashPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearSession = useAuthStore((s) => s.clearSession);
  const terminal = useTerminalStore((s) => s.terminal);
  const unpairTerminal = useTerminalStore((s) => s.unpair);

  const currentSession = useCurrentCashSession();
  const openSession = useOpenCashSession();
  const [openedAmount, setOpenedAmount] = useState("0.00");

  // Si ya hay sesión abierta, ir al POS.
  useEffect(() => {
    if (currentSession.data) {
      navigate("/", { replace: true });
    }
  }, [currentSession.data, navigate]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    openSession.mutate(
      { opened_amount: openedAmount },
      {
        onSuccess: () => navigate("/", { replace: true }),
      },
    );
  };

  const errorMsg =
    openSession.error instanceof ApiError
      ? openSession.error.message
      : openSession.error?.message;

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-lg font-semibold text-slate-800">Apertura de caja</h1>
            <p className="text-sm text-slate-500">Cajero: {user?.full_name}</p>
          </div>
          {terminal && (
            <button
              type="button"
              onClick={() => {
                unpairTerminal();
                navigate("/select-terminal", { replace: true });
              }}
              title={`Cambiar caja (actual: ${terminal.code})`}
              className="flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-800 hover:border-indigo-300 hover:bg-indigo-100"
            >
              <Monitor className="h-3.5 w-3.5" />
              <span className="font-mono">{terminal.code}</span>
              <RefreshCw className="h-3 w-3 opacity-60" />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            clearSession();
            unpairTerminal();
            navigate("/login", { replace: true });
          }}
          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-100"
        >
          <LogOut className="h-4 w-4" /> Salir
        </button>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-10">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-md rounded-2xl bg-white p-8 shadow-sm"
        >
          <div className="mb-6 flex items-center gap-3">
            <div className="rounded-xl bg-emerald-100 p-3 text-emerald-700">
              <Banknote className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Iniciar turno</h2>
              <p className="text-sm text-slate-500">
                Cuenta el efectivo inicial en caja para arrancar.
              </p>
            </div>
          </div>

          <label className="mb-2 block text-sm font-medium text-slate-700">
            Monto inicial en efectivo (S/)
          </label>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={openedAmount}
            onChange={(e) => setOpenedAmount(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-4 py-3 text-2xl font-medium text-slate-900 outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
            placeholder="0.00"
            autoFocus
          />

          {errorMsg && (
            <p className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {errorMsg}
            </p>
          )}

          <button
            type="submit"
            disabled={openSession.isPending}
            className="mt-6 w-full rounded-xl bg-slate-900 py-4 text-base font-semibold text-white shadow-sm hover:bg-slate-800 active:bg-slate-950 disabled:opacity-50"
          >
            {openSession.isPending ? "Abriendo..." : "Abrir caja"}
          </button>
        </form>
      </main>
    </div>
  );
}
